import * as PIXI from "pixi.js";
import { Spine } from "@esotericsoftware/spine-pixi-v7";
// import { ClippingAttachment } from "@esotericsoftware/spine-core";
import { Skin } from "@esotericsoftware/spine-core";
import TickerTimeout from "../../utils/TickerTimeout";
import SpineAtlasUploader from "../../utils/SpineAtlasUploader";
import { queueMeshWarm } from "../Shared/spineMeshWarm";
import { Settings } from "../../settings";
import { FEATURES_CONFIG } from "../../featuresConfig";

/**
 * cellAnticipation — the Hold & Win last cells' anticipation (Vladimir,
 * 2026-10-07: "the same thing we have on base game, only for 1 or 2 cells").
 *
 * The base game's per-reel chain (anticipationRunner), cell by cell:
 *
 *   the respin starts                    -> chain cell 1: in -> loop (x LOOPS)
 *   ...its in + loops elapse             -> cell 1 stops, its out plays
 *   out finishes                         -> chain cell 2: in -> loop ...
 *   last chain cell's out ends           -> any cells after the chain stop
 *
 * It starts WITH the spin (cellAnticipationApplies — how many cells are left
 * is known before the answer); the answer only sets each landing and where
 * the chain ends (cellAnticipationPlan). HoldAndWinBoard runs the turns
 * (beginAnticipation, land); CellAnticipationEffect is drawn on the cell.
 *
 * Timing runs on TickerTimeouts from the clips' own durations, never on spine
 * listeners — a missing spine shortens the wait, it never leaves a cell
 * spinning.
 */

const CLIP_IN = "anticipation_in";
const CLIP_LOOP = "anticipation_loop";
const CLIP_OUT = "anticipation_out";
// The base game's anticipation spine (GameViewSimpleController
// SPINE_CONTRACT.ANTICIPATION_SPINE).
const SKELETON = "anticipation-skeleton";
const ATLAS = "anticipation-atlas";

const config = () => FEATURES_CONFIG.HOLD_AND_WIN_SPIN?.CELL_ANTICIPATION ?? {};

// // The spine's own masks (anticipation_clip_1 / _2) are the reel column plus
// // this margin on every side, units (hand-loosened in anticipation.json,
// // 2026-10-07: "a couple of pixels looser … I just want to see all 4 edges").
// const CLIP_MARGIN = 12;
// ── All 4 edges are LINES now (2026-10-07: "use the same thing we use for
// left and right on top and bottom") ── anticipation.json's side glow line
// (ant_line) turned 90° for the top and bottom (ant_line3 / ant_line4); the
// masks are back to the column.
const EDGE_LINE_SLOTS = ["ant_line3", "ant_line4"];
const EDGE_LINE_ATTACHMENT = "ant_line";

/**
 * Does this respin get the anticipation? Known the moment the cells start
 * spinning — 1 .. MAX_CELLS of them — so the chain starts WITH the spin
 * (Pot Collection: "works instantly during spin"), before the answer.
 *
 * @param {Array<[number, number]>} cells The spinning cells.
 */
export const cellAnticipationApplies = (cells) => {
  const cfg = config();
  return (
    !!cfg.ENABLED &&
    Array.isArray(cells) &&
    cells.length > 0 &&
    cells.length <= (cfg.MAX_CELLS ?? 2)
  );
};

/**
 * The respin's chain, decided ONCE from the server's response — never
 * recomputed as cells land (Pot Collection's false anticipations came from a
 * recount after some cells had landed).
 *
 * Every empty cell spins, so the grid fills exactly when every spinning cell
 * lands something. The chain is the LAST 1 .. MAX_CELLS cells in the board's
 * stop order, and it happens only if every cell BEFORE them lands something:
 * then, the moment the last of those lands (the 38th coin of 40), the cells
 * still spinning go straight into the anticipation, in the same respin
 * (Vladimir, 2026-10-07: "as soon as the 38th lands, those 2 last must
 * enter anticipation"). A miss before them — the grid cannot fill this time,
 * no chain. With only 1 .. MAX_CELLS spinning, `startAt` is 0: the chain
 * began with the spin (cellAnticipationApplies).
 *
 * EVERY one of those last cells takes its turn — the first finishes, the
 * next enters — whatever the one before it landed (Vladimir, 2026-10-07:
 * "both need to enter, first finishes other enters"). Was: the chain ended
 * with the first cell that landed empty.
 *
 * @param {Array<[number, number]>} cells     The spinning cells, stop order.
 * @param {Array<Array>}            nextKinds The grid after the response.
 * @returns {{ startAt: number, cells: Array<[number, number]> }|null}
 *          startAt: how many cells stop normally first; cells: the chain.
 */
export const cellAnticipationPlan = (cells, nextKinds) => {
  const cfg = config();
  if (!cfg.ENABLED || !Array.isArray(cells) || cells.length === 0) return null;
  const landsSomething = ([c, r]) => !!nextKinds?.[c]?.[r];
  const startAt = Math.max(0, cells.length - (cfg.MAX_CELLS ?? 2));
  for (let i = 0; i < startAt; i++) {
    if (!landsSomething(cells[i])) return null; // the grid cannot fill this respin
  }
  // const chain = [];
  // for (let i = startAt; i < cells.length; i++) {
  //   chain.push(cells[i]);
  //   if (!landsSomething(cells[i])) break; // lands empty — the hope ends here
  // }
  const chain = cells.slice(startAt);
  return { startAt, cells: chain };
};

/**
 * The anticipation drawn on ONE cell at a time: the base game's reel
 * anticipation spine, fitted onto the cell (its column is SYMBOL_WIDTH wide
 * and REEL_ROWS cells tall), in the cell's centre. SKIN picks the animator's
 * cell skin once it exists.
 */
export class CellAnticipationEffect {
  /** @param {PIXI.Container} layer The overlay layer it draws in. */
  constructor(layer) {
    this.layer = layer;
    this.holder = null;
    this.spine = null;
    this._hideTimer = null;
    // // slot name -> { copy, vertices } — this spine's OWN copy of each mask.
    // this._clips = new Map();
    // The skin it was built with (SKIN, or the default) — _fitEdgeLines.
    this._baseSkin = null;
  }

  // /**
  //  * On a cell the column is squashed to about a fifth of its height, so the
  //  * masks' CLIP_MARGIN would be ~2 px top and bottom against ~8 px at the
  //  * sides. This spine's own COPY of each mask (the skeleton data is shared
  //  * with the base game's — never touched) gets the vertical margin that
  //  * makes it the same pixels as the horizontal one. Re-set after every
  //  * setToSetupPose, which puts the shared attachment back.
  //  */
  // _loosenClips(sx, sy) {
  //   const skeleton = this.spine?.skeleton;
  //   if (!skeleton || !(sx > 0) || !(sy > 0)) return;
  //   const extraY = CLIP_MARGIN * (sx / sy - 1);
  //   for (const slot of skeleton.slots) {
  //     const name = slot.data.name;
  //     let entry = this._clips.get(name);
  //     if (!entry) {
  //       const att = slot.getAttachment();
  //       if (!(att instanceof ClippingAttachment)) continue;
  //       entry = { copy: att.copy(), vertices: att.vertices.slice() };
  //       this._clips.set(name, entry);
  //     }
  //     const v = entry.vertices;
  //     let minY = Infinity;
  //     let maxY = -Infinity;
  //     for (let i = 1; i < v.length; i += 2) {
  //       minY = Math.min(minY, v[i]);
  //       maxY = Math.max(maxY, v[i]);
  //     }
  //     const midY = (minY + maxY) / 2;
  //     const out = entry.copy.vertices;
  //     for (let i = 0; i < v.length; i += 2) {
  //       out[i] = v[i];
  //       out[i + 1] = v[i + 1] + (v[i + 1] > midY ? extraY : -extraY);
  //     }
  //     slot.setAttachment(entry.copy);
  //   }
  // }

  _ensureSpine() {
    if (this.spine && !this.spine.destroyed) return this.spine;
    const assets = PIXI.Assets.cache;
    if (!this.layer || !assets.has(SKELETON) || !assets.has(ATLAS)) return null;
    try {
      const spine = Spine.from({ skeleton: SKELETON, atlas: ATLAS });
      spine.name = "hold_and_win_cell_anticipation";
      const skin = config().SKIN;
      if (skin && spine.skeleton.data.findSkin(skin)) {
        spine.skeleton.setSkinByName(skin);
        spine.skeleton.setSlotsToSetupPose();
      }
      const holder = new PIXI.Container();
      holder.name = "hold_and_win_cell_anticipation_holder";
      holder.visible = false;
      holder.addChild(spine);
      this.layer.addChild(holder);
      this.holder = holder;
      this.spine = spine;
      return spine;
    } catch (error) {
      console.error("[H&W] cell anticipation spine failed:", error);
      return null;
    }
  }

  /**
   * On a cell the column is squashed to about a fifth of its height, so the
   * TOP and BOTTOM lines (their thickness runs vertically) would be a fifth
   * as thick as the sides. This spine gets its OWN skin: the shared one, with
   * copies of those two lines thickened by sx / sy — the clips key the
   * attachment by name every loop, so the skin is where it has to live. The
   * skeleton data is shared with the base game's spine and never touched.
   */
  _fitEdgeLines(sx, sy) {
    const skeleton = this.spine?.skeleton;
    if (!skeleton || !(sx > 0) || !(sy > 0)) return;
    const data = skeleton.data;
    if (!this._baseSkin) this._baseSkin = skeleton.skin ?? data.defaultSkin;
    const base = this._baseSkin;
    const skin = new Skin("hold_and_win_cell");
    skin.addSkin(base);
    if (base !== data.defaultSkin && data.defaultSkin) skin.addSkin(data.defaultSkin);
    for (const name of EDGE_LINE_SLOTS) {
      const slot = data.findSlot(name);
      if (!slot) continue;
      const att =
        base.getAttachment(slot.index, EDGE_LINE_ATTACHMENT) ??
        data.defaultSkin?.getAttachment(slot.index, EDGE_LINE_ATTACHMENT);
      if (!att?.copy) continue;
      const copy = att.copy();
      copy.scaleX = att.scaleX * (sx / sy);
      copy.updateRegion?.();
      skin.setAttachment(slot.index, EDGE_LINE_ATTACHMENT, copy);
    }
    skeleton.setSkin(skin);
  }

  /**
   * Built when the room mounts, not on the first anticipation's frame
   * (2026-10-07, the base game's stutter): the spine, its atlas on the GPU
   * (shared with the base game's — a no-op when that already did it).
   */
  prepare() {
    if (this._ensureSpine()) {
      SpineAtlasUploader.upload("anticipation");
      // Every clip's meshes too, while hidden (Games/Shared/spineMeshWarm).
      queueMeshWarm({ spine: this.spine, isBusy: () => !!this.holder?.visible });
    }
  }

  _clipMs(name, fallback) {
    const anim = this.spine?.skeleton?.data?.findAnimation(name);
    return anim ? anim.duration * 1000 : fallback;
  }

  /** The clips' lengths, ms — what the chain's turns are timed on. */
  timing() {
    this._ensureSpine();
    return {
      inMs: this._clipMs(CLIP_IN, 0),
      loopMs: this._clipMs(CLIP_LOOP, 1000),
      outMs: this._clipMs(CLIP_OUT, 0),
    };
  }

  /**
   * in -> loop on the cell centred at (x, y), `cellW` x `cellH` px.
   */
  play(x, y, cellW, cellH) {
    const spine = this._ensureSpine();
    if (!spine) return;
    this._hideTimer?.clear();
    this._hideTimer = null;
    const rows = config().REEL_ROWS ?? 4;
    this.holder.position.set(x, y);
    // this.holder.scale.set(
    //   cellW / Settings.SYMBOL_WIDTH,
    //   cellH / (Settings.SYMBOL_HEIGHT * rows),
    // );
    const sx = cellW / Settings.SYMBOL_WIDTH;
    const sy = cellH / (Settings.SYMBOL_HEIGHT * rows);
    this.holder.scale.set(sx, sy);
    const { state, skeleton } = spine;
    state.clearTracks();
    // this._loosenClips(sx, sy);
    this._fitEdgeLines(sx, sy);
    skeleton.setToSetupPose();
    const data = skeleton.data;
    if (data.findAnimation(CLIP_IN)) {
      state.setAnimation(0, CLIP_IN, false);
      if (data.findAnimation(CLIP_LOOP)) state.addAnimation(0, CLIP_LOOP, true, 0);
    } else if (data.findAnimation(CLIP_LOOP)) {
      state.setAnimation(0, CLIP_LOOP, true);
    }
    // First frame NOW — the setup pose never draws (the reel frame's flash).
    state.update(0);
    state.apply(skeleton);
    this.holder.visible = true;
  }

  /** out, then hidden — on the clip's length, not its listener. */
  stop() {
    const spine = this.spine;
    if (!spine || !this.holder?.visible) return;
    const outMs = this._clipMs(CLIP_OUT, 0);
    if (!outMs) {
      this.hide();
      return;
    }
    spine.state.setAnimation(0, CLIP_OUT, false);
    this._hideTimer?.clear();
    this._hideTimer = new TickerTimeout(() => {
      this._hideTimer = null;
      this.hide();
    }, outMs);
  }

  /** At once: nothing on screen, nothing playing. */
  hide() {
    this._hideTimer?.clear();
    this._hideTimer = null;
    if (this.holder) this.holder.visible = false;
    if (this.spine && !this.spine.destroyed) {
      this.spine.state.clearTracks();
      this.spine.skeleton.setToSetupPose();
    }
  }

  destroy() {
    this.hide();
    if (this.holder && !this.holder.destroyed) this.holder.destroy({ children: true });
    this.holder = null;
    this.spine = null;
  }
}
